import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { API_get_schedule } from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportCsvDownloadButton from "../ReportCsvDownloadButton";
import ReportEmailToggle from "../ReportEmailToggle";
import {
  displayName,
  formatShiftTime,
  unwrapPayload,
} from "./_shared/laborMath";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Snap a date back to the start of the business week (week-start day varies
// by company — some use Sunday, some Monday).
function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

export default function WeeklySimpleScheduleReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
  userId,
}) {
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, homeStore),
    [storeNames, homeStore]
  );

  const [weekStart, setWeekStart] = useState(() =>
    snapToWeekStart(new Date(), weekStartDayIndex)
  );

  // If the business week-start setting changes (e.g. store switch), re-snap
  // the current week so the view stays aligned.
  useEffect(() => {
    setWeekStart((prev) => snapToWeekStart(prev, weekStartDayIndex));
  }, [weekStartDayIndex]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 6);
    return d;
  }, [weekStart]);

  const incrementWeek = useCallback((currentStart, direction) => {
    const next = new Date(currentStart);
    next.setDate(next.getDate() + direction * 7);
    setWeekStart(next);
  }, []);

  const setWeekStartDate = useCallback(
    (newDate) => {
      setWeekStart(snapToWeekStart(newDate, weekStartDayIndex));
    },
    [weekStartDayIndex]
  );

  const weekDates = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
      }),
    [weekStart]
  );

  const [schedule, setSchedule] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Bump to force a refetch (e.g. retry after error) without changing
  // weekStart/homeStore.
  const [retryNonce, setRetryNonce] = useState(0);

  // Effect-scoped `cancelled` flag handles two problems:
  //   1. Unmount mid-fetch (user clicks Back) — don't setState on a dead
  //      component.
  //   2. Stale results when the user flips weeks quickly — only the latest
  //      effect's fetch is allowed to apply its result.
  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await getRightworkAPI(API_get_schedule, {
          store_id: homeStore,
          lower_bound_date: dayjs(weekStart).format("YYYY-MM-DD"),
          upper_bound_date: dayjs(weekEnd).format("YYYY-MM-DD"),
          should_return_all_stores: false,
        });
        if (cancelled) return;
        // get_schedule uses http_redux_response → unwrap to the payload,
        // then pull `.schedule` if present (defensive: also accept a bare
        // array body).
        const payload = unwrapPayload(res);
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.schedule)
          ? payload.schedule
          : [];
        setSchedule(list);
      } catch (e) {
        if (cancelled) return;
        setError(
          "Couldn't load schedule. Try again or pick a different week."
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, weekStart, weekEnd, retryNonce]);

  const handleRetry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  // Build a map of employee_id → { name, days: [shifts per business-week day], total }
  const rows = useMemo(() => {
    const byEmployee = new Map();
    const startMs = weekStart.getTime();

    for (const shift of schedule) {
      const stm = shift.store_team_member;
      if (!stm) continue;
      const key = stm.id;
      if (!byEmployee.has(key)) {
        byEmployee.set(key, {
          id: key,
          name: displayName(stm),
          days: Array.from({ length: 7 }, () => []),
          total: 0,
        });
      }
      const row = byEmployee.get(key);
      // shift.date is a YYYY-MM-DD string; treat as local midnight
      const shiftMs = new Date(shift.date + "T00:00:00").getTime();
      const dayIndex = Math.round((shiftMs - startMs) / 86400000);
      if (dayIndex < 0 || dayIndex > 6) continue;
      row.days[dayIndex].push(shift);
      const hours = Number(shift.hours ?? shift.shift_length ?? 0);
      if (!Number.isNaN(hours)) row.total += hours;
    }

    return Array.from(byEmployee.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [schedule, weekStart]);

  const weekTotal = useMemo(
    () => rows.reduce((sum, r) => sum + r.total, 0),
    [rows]
  );

  const filters = (
    <div className="flex items-center">
      <WeekSelector
        start_date={weekStart}
        end_date={weekEnd}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
      />
    </div>
  );

  return (
    <PrebuiltReportShell
      actions={
        <>
          <ReportEmailToggle
            reportId={report.id}
            userId={userId}
            schedule={report.emailSchedule}
          />
          <ReportCsvDownloadButton
            reportId="weekly-simple-schedule"
            storeId={homeStore}
            start={dayjs(weekStart).format("YYYY-MM-DD")}
            end={dayjs(weekEnd).format("YYYY-MM-DD")}
          />
        </>
      }
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center">
          No published shifts for this week.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left font-semibold text-slate-700 px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                  Employee
                </th>
                {weekDates.map((d, i) => (
                  <th
                    key={i}
                    className="text-left font-semibold text-slate-700 px-3 py-2 min-w-[120px]"
                  >
                    <div>{DAY_NAMES[d.getDay()]}</div>
                    <div className="text-[10px] text-slate-500 font-normal">
                      {dayjs(d).format("M/D/YYYY")}
                    </div>
                  </th>
                ))}
                <th className="text-right font-semibold text-slate-700 px-3 py-2 min-w-[80px]">
                  Total Hours
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="group border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 z-10 bg-white group-hover:bg-slate-50">
                    {row.name}
                  </td>
                  {row.days.map((shifts, i) => (
                    <td key={i} className="px-3 py-2 align-top">
                      {shifts.length === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <div className="space-y-1">
                          {shifts.map((s) => (
                            <div key={s.id} className="text-slate-700">
                              {formatShiftTime(s.shift_start)} to{" "}
                              {formatShiftTime(s.shift_end)}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">
                    {row.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200">
                <td
                  colSpan={8}
                  className="px-3 py-2 text-right font-semibold text-slate-700"
                >
                  Total Hours
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">
                  {weekTotal.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </PrebuiltReportShell>
  );
}
